import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Radio,
  Upload,
  message,
  Space,
  Typography,
  Divider,
  Image,
  Row,
  Col,
  Tag,
  Alert,
} from 'antd';
import {
  UploadOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  SaveOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const DEFAULT = {
  enabled: true,
  mediaType: 'video',
  title: 'Inside John Strategy Club',
  subtitle: 'Meet our team, workspace, and live trading moments',
  eyebrow: 'INSIDE THE DESK',
  videoUrl: '',
  posterUrl: '',
  images: [],
  autoplay: true,
  muted: true,
  loop: true,
};

const abs = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${getImageUrl(u)}`;
};

const PromoConfig = () => {
  const [form] = Form.useForm();
  const [config, setConfig] = useState(DEFAULT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/promo');
        const data = res.data?.data || DEFAULT;
        setConfig({ ...DEFAULT, ...data });
        form.setFieldsValue({ ...DEFAULT, ...data });
      } catch (e) {
        message.error('加载配置失败: ' + (e.response?.data?.message || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [form]);

  const doSave = async (patch = {}) => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const merged = { ...config, ...values, ...patch };
      const res = await api.put('/promo', merged);
      const saved = res.data?.data || merged;
      setConfig(saved);
      form.setFieldsValue(saved);
      message.success('已保存');
    } catch (e) {
      if (e.errorFields) return;
      message.error('保存失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const uploadOne = async (file, kind) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/promo/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 5 * 60 * 1000,
    });
    if (!res.data?.success) throw new Error(res.data?.message || 'upload failed');
    return res.data.data; // { url, kind, filename }
  };

  const handleVideoUpload = async ({ file }) => {
    setUploadingVideo(true);
    try {
      const info = await uploadOne(file, 'video');
      const next = { ...config, videoUrl: info.url, mediaType: 'video' };
      setConfig(next);
      form.setFieldsValue({ videoUrl: info.url, mediaType: 'video' });
      await doSave({ videoUrl: info.url, mediaType: 'video' });
    } catch (e) {
      message.error('视频上传失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploadingVideo(false);
    }
    return false;
  };

  const handlePosterUpload = async ({ file }) => {
    setUploadingPoster(true);
    try {
      const info = await uploadOne(file, 'image');
      setConfig((c) => ({ ...c, posterUrl: info.url }));
      form.setFieldsValue({ posterUrl: info.url });
      await doSave({ posterUrl: info.url });
    } catch (e) {
      message.error('封面上传失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploadingPoster(false);
    }
    return false;
  };

  const handleImageUpload = async ({ file }) => {
    setUploadingImage(true);
    try {
      const info = await uploadOne(file, 'image');
      const nextImages = [...(config.images || []), info.url];
      const next = { ...config, images: nextImages, mediaType: 'images' };
      setConfig(next);
      form.setFieldsValue({ images: nextImages, mediaType: 'images' });
      await doSave({ images: nextImages, mediaType: 'images' });
    } catch (e) {
      message.error('图片上传失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploadingImage(false);
    }
    return false;
  };

  const removeImage = (url) => {
    const next = (config.images || []).filter((u) => u !== url);
    setConfig((c) => ({ ...c, images: next }));
    form.setFieldsValue({ images: next });
    doSave({ images: next });
  };

  const clearVideo = () => {
    setConfig((c) => ({ ...c, videoUrl: '' }));
    form.setFieldsValue({ videoUrl: '' });
    doSave({ videoUrl: '' });
  };

  const clearPoster = () => {
    setConfig((c) => ({ ...c, posterUrl: '' }));
    form.setFieldsValue({ posterUrl: '' });
    doSave({ posterUrl: '' });
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 8 }}>
        <VideoCameraOutlined /> 首页企业宣传区块
      </Title>
      <Paragraph type="secondary">
        在首页 Hero 下方展示企业宣传视频或图片轮播。可上传 MP4 视频（&le; 200MB）或多张图片。
      </Paragraph>

      <Form form={form} layout="vertical" initialValues={config}>
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            <Card title="基本配置" loading={loading} style={{ marginBottom: 16 }}>
              <Form.Item label="启用该区块" name="enabled" valuePropName="checked">
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>

              <Form.Item label="展示形式" name="mediaType">
                <Radio.Group>
                  <Radio.Button value="video">
                    <VideoCameraOutlined /> 视频
                  </Radio.Button>
                  <Radio.Button value="images">
                    <PictureOutlined /> 图片轮播
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item label="小标 (eyebrow)" name="eyebrow">
                <Input placeholder="例：INSIDE THE DESK" />
              </Form.Item>

              <Form.Item label="标题" name="title" rules={[{ required: true, message: '请输入标题' }]}>
                <Input placeholder="e.g. Inside John Strategy Club" />
              </Form.Item>

              <Form.Item label="副标题" name="subtitle">
                <Input.TextArea rows={2} placeholder="一句话简介" />
              </Form.Item>

              <Divider>视频播放选项</Divider>

              <Space size={24}>
                <Form.Item label="自动播放" name="autoplay" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="默认静音" name="muted" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="循环播放" name="loop" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Space>

              <Alert
                type="info"
                showIcon
                message="提示：自动播放需要开启「默认静音」才会被浏览器允许。"
              />
            </Card>

            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => doSave()}
              >
                保存配置
              </Button>
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              title={
                <span>
                  <VideoCameraOutlined /> 宣传视频
                </span>
              }
              style={{ marginBottom: 16 }}
            >
              <Form.Item label="视频 URL" name="videoUrl">
                <Input placeholder="/uploads/promo/xxx.mp4 或完整 URL" />
              </Form.Item>

              <Space wrap>
                <Upload
                  accept="video/mp4,video/webm,video/quicktime"
                  showUploadList={false}
                  beforeUpload={(file) => handleVideoUpload({ file })}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingVideo}>
                    上传新视频
                  </Button>
                </Upload>
                {config.videoUrl && (
                  <Button danger icon={<DeleteOutlined />} onClick={clearVideo}>
                    清空视频
                  </Button>
                )}
              </Space>

              {config.videoUrl && (
                <div style={{ marginTop: 16 }}>
                  <Tag color="blue">当前视频</Tag>
                  <video
                    src={abs(config.videoUrl)}
                    controls
                    style={{ width: '100%', marginTop: 8, borderRadius: 6, background: '#000' }}
                  />
                </div>
              )}

              <Divider />

              <Form.Item label="视频封面 URL (可选)" name="posterUrl">
                <Input placeholder="/uploads/promo/xxx.jpg" />
              </Form.Item>
              <Space wrap>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => handlePosterUpload({ file })}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingPoster}>
                    上传封面
                  </Button>
                </Upload>
                {config.posterUrl && (
                  <Button danger icon={<DeleteOutlined />} onClick={clearPoster}>
                    清空封面
                  </Button>
                )}
              </Space>
              {config.posterUrl && (
                <div style={{ marginTop: 12 }}>
                  <Image
                    src={abs(config.posterUrl)}
                    alt="poster"
                    style={{ maxWidth: 220, borderRadius: 6 }}
                  />
                </div>
              )}
            </Card>

            <Card
              title={
                <span>
                  <PictureOutlined /> 图片轮播（选择「图片轮播」模式时展示）
                </span>
              }
            >
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => handleImageUpload({ file })}
              >
                <Button icon={<UploadOutlined />} loading={uploadingImage}>
                  添加图片
                </Button>
              </Upload>

              <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
                {(config.images || []).length === 0 && (
                  <Col span={24}>
                    <Text type="secondary">还没有上传任何图片。</Text>
                  </Col>
                )}
                {(config.images || []).map((url) => (
                  <Col xs={12} md={8} key={url}>
                    <div
                      style={{
                        position: 'relative',
                        aspectRatio: '16 / 10',
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: `#f0f2f5 url(${abs(url)}) center/cover no-repeat`,
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={() => removeImage(url)}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default PromoConfig;
